import { useTRPC } from '@/utils/trpc';
import { medicationRouteToCodeableConcept } from '@liza/fhir-utils';
import { medicationInputFromProduct, type MedicationSearchQueries } from '@liza/ui/components/records';
import { SHARED_SCHEDULE_TYPES } from '@liza/ui/components/TaskTimingPicker';
import { MedicationForm, type MedicationFormValues } from '@liza/ui/components/records/MedicationForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useMedicationFormDataSources } from '@liza/ui/hooks/useMedicationFormDataSources';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type Props = {
  medplumPatientId: string;
  /** The patient's managing organization, which selects the ScriptSure practice the drug-catalog
   * search runs under. */
  organizationId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddMedicationDialog = ({
  medplumPatientId,
  organizationId,
  isOpen,
  onOpenChange,
}: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  // The product picker is the shared ScriptSure two-step search; the data-sources hook owns the
  // sig → dose/unit/route derivation (falling back to the dose form) for the selected coded product.
  const { derivedDose, derivedUnit, derivedRoute, unitOptions, routeOptions, isDoseFormFetching, setSelectedProduct } =
    useMedicationFormDataSources({
      enabled: isOpen,
      valueSetExpand: (input) => trpc.valueSet.expand.queryOptions(input),
      codeSystemLookup: (input) => trpc.codeSystem.lookup.queryOptions(input),
    });

  // The org scopes the ScriptSure catalog search to the patient's practice.
  const medicationSearch: MedicationSearchQueries = {
    searchMedications: (input) => trpc.erx.searchMedications.queryOptions({ ...input, organizationId }),
    searchFallbackFormulations: (input) => trpc.erx.searchFallbackFormulations.queryOptions(input),
  };

  useEffect(() => {
    if (!isOpen) setSelectedProduct(null);
  }, [isOpen, setSelectedProduct]);

  const { mutateAsync: createMedicationRequest, isPending: isCreating } = useMutation(
    trpc.patient.medicationRequest.createFromInput.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.medicationRequest.list.queryKey({ medplumPatientId }),
        });
      },
    }),
  );

  const handleCreate = useCallback(
    async (values: MedicationFormValues): Promise<void> => {
      try {
        await createMedicationRequest({
          medplumPatientId,
          input: {
            medication: medicationInputFromProduct(values.product),
            doseQuantity: String(values.dose),
            doseForm: { text: values.units },
            doseMethod: medicationRouteToCodeableConcept(values.route),
            note: values.note || undefined,
            scheduling: values.timing ?? undefined,
            asNeeded: values.asNeeded || undefined,
          },
        });
        toast.success(t('medicationAdded'));
        onOpenChange(false);
      } catch (error) {
        toast.error(`${t('addMedicationFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createMedicationRequest, medplumPatientId, onOpenChange, t],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('addMedication')}</DialogTitle>
        </DialogHeader>
        <MedicationForm
          className="p-0"
          // `SHARED_SCHEDULE_TYPES` minus Once: this dialog writes a records `MedicationStatement`
          // (`medicationRequest.createFromInput`), whose input has no due-date concept — a records
          // medication is a statement of what the patient takes, not a scheduled care-plan item.
          // Once belongs to the care-plan medication flow (#8100).
          supportedScheduleTypes={SHARED_SCHEDULE_TYPES.filter((type) => type !== 'once')}
          isCreating={isCreating}
          onClose={() => onOpenChange(false)}
          onCreate={handleCreate}
          medicationSearch={medicationSearch}
          unitOptions={unitOptions}
          routeOptions={routeOptions}
          defaultDose={{ value: derivedDose }}
          defaultUnit={{ value: derivedUnit, isLoading: isDoseFormFetching }}
          defaultRoute={{ value: derivedRoute, isLoading: isDoseFormFetching }}
          onProductChange={setSelectedProduct}
        />
      </DialogContent>
    </Dialog>
  );
};
